import { getAllBlocks } from "./registry";
import type { ContentBlock, ContentBlockKind, ContentTag } from "./types";

export type SearchResult = {
  block: ContentBlock;
  score: number;
};

/**
 * Score how well `text` matches `query`.
 * Exact > starts-with > word-starts-with > contains.
 */
function scoreMatch(text: string, query: string): number {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();

  if (lower === q) return 100;
  if (lower.startsWith(q)) return 90;
  if (lower.split(/\s+/).some((w) => w.startsWith(q))) return 80;
  if (lower.includes(q)) return 60;
  return 0;
}

function scoreBlock(block: ContentBlock, query: string): number {
  const queries = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (queries.length === 0) return 0;

  let total = 0;

  for (const q of queries) {
    let max = 0;

    // Title (highest weight)
    max = Math.max(max, scoreMatch(block.title, q));

    // Subtitle
    if (block.subtitle) {
      max = Math.max(max, scoreMatch(block.subtitle, q) * 0.7);
    }

    // Section titles for articles
    if (block.kind === "article") {
      const body = block.body as {
        sections?: { title: string }[];
        introduction?: string;
      };
      if (body.introduction) {
        max = Math.max(max, scoreMatch(body.introduction, q) * 0.6);
      }
      if (body.sections) {
        for (const s of body.sections) {
          max = Math.max(max, scoreMatch(s.title, q) * 0.5);
        }
      }
    }

    // Tags
    for (const tag of block.tags) {
      max = Math.max(max, scoreMatch(tag, q) * 0.4);
    }

    total += max;
  }

  return total / queries.length;
}

const SCORE_THRESHOLD = 20;

/**
 * Search all content blocks in the registry.
 * Optionally filter by kind and/or tags.
 * Returns results sorted by score descending.
 */
export function searchContent(
  query: string,
  opts?: {
    kind?: ContentBlockKind;
    tags?: ContentTag[];
    limit?: number;
  },
): SearchResult[] {
  if (!query.trim()) return [];

  let blocks = getAllBlocks();

  if (opts?.kind) {
    blocks = blocks.filter((b) => b.kind === opts.kind);
  }
  if (opts?.tags && opts.tags.length > 0) {
    blocks = blocks.filter((b) => opts.tags!.some((t) => b.tags.includes(t)));
  }

  const results: SearchResult[] = [];
  for (const block of blocks) {
    const score = scoreBlock(block, query);
    if (score > SCORE_THRESHOLD) {
      results.push({ block, score });
    }
  }

  results.sort((a, b) => b.score - a.score);

  if (opts?.limit) {
    return results.slice(0, opts.limit);
  }

  return results;
}
